import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client.ts';
import { queryDefaults } from './queries.ts';
import { useSession } from '../auth/session.tsx';

/** One report of where the client is said to be (093). Append-only: never edited. */
export interface WhereaboutsReport {
  id: string;
  reported_region: string;
  learned_on: string | null;
  there_since: string | null;
  how_learned: string;
  recorded_by: string | null;
  created_at: string;
}

/** The place of the most recent located visit. `approximate`: the load's, one location a year. */
export interface LastKnown {
  visit_date: string;
  approximate: boolean;
  place: string;
}

export interface WhereaboutsReports {
  whereabouts_unknown: boolean;
  reports: WhereaboutsReport[];
  last_known: LastKnown | null;
  /** How the last placement with a named ending ended (093): "Left without notice". */
  last_end_reason: string | null;
  end_reasons: { residence_id: string; end_reason: string }[];
}

export interface NewWhereaboutsReport {
  reported_region: string;
  learned_on?: string;
  there_since?: string;
  how_learned: string;
}

export function useWhereaboutsReports(clientId: string) {
  const { getToken } = useSession();
  return useQuery({
    queryKey: ['client', clientId, 'whereabouts-reports'],
    queryFn: () => apiFetch<WhereaboutsReports>(`/clients/${clientId}/whereabouts-reports`, getToken),
    ...queryDefaults,
  });
}

export function useRecordWhereaboutsReport(clientId: string) {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: NewWhereaboutsReport) =>
      apiFetch<{ id: string }>(`/clients/${clientId}/whereabouts-reports`, getToken, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['client', clientId] });
    },
  });
}
